<template>
    <div>
        <div v-if="loading">
            <loading/>
        </div>

        <div v-else>
            <b-alert variant="danger" :show="hasErrors">
                <span v-html="errorMessage"></span>
            </b-alert>
            <!-- filter -->
            <b-row v-if="!hasErrors" class="mb-3">
                <b-col sm="4" class="my-1">
                    <charge-template-handler
                        :charge-template-id="chargeTemplateId"
                        :client-id="client"
                        @template-is-updated="getItems"
                    />
                </b-col>
                <b-col sm="3" class="my-1">
                    <b-button
                        size="sm"
                        variant="primary"
                        href="/parametrage#/modeles-de-plan-de-charge"
                        title="Créer un nouveau plan de charge type"
                    >
                        <i class="fas fa-plus fa-fw mr-2"></i>Plan de charge
                        type
                    </b-button>
                </b-col>
                <b-col sm="4" offset-sm="1" class="my-1">
                    <b-form-group label-cols-sm="3" class="mb-0">
                        <b-input-group size="sm">
                            <b-form-input
                                v-model="filter"
                                placeholder="Entrer la valeur recherchée"/>
                            <b-input-group-append>
                                <b-button
                                    :disabled="!filter"
                                    @click="filter = ''"
                                >Effacer
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                </b-col>
            </b-row>
            <!-- /filter -->

            <!-- Table -->
            <b-table
                responsive
                small
                :items="filteredItems"
                :fields="fields"
                :filter="filter"
                :per-page="perPage"
                :current-page="currentPage"
                primary-key="id"
                @filtered="onFiltered"
            >
                <template #cell(menu)="data">
                    <drop-menu-action :menu="data.item.menu"/>
                </template>
                <template #cell(deadline_month_y0)="data">
                    <action-date-form
                        :action="data.item"
                        year="Y0"
                        @date-is-updated="getItems"/>
                </template>
                <template #cell(deadline_month_y1)="data">
                    <action-date-form
                        :action="data.item"
                        year="Y1"
                        @date-is-updated="getItems"/>
                </template>
                <template #cell(duration)="data">
                    <duration-form
                        :action="data.item"
                        @duration-updated="getItems"
                    />
                </template>
            </b-table>
            <!-- /Table -->

            <!-- Pagination -->
            <b-row>
                <b-col md="3" class="my-1 text-center">
                    <b-pagination
                        v-if="totalRows > 0"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        class="my-0"
                        size="sm"
                    />
                </b-col>
                <b-col md="6" class="my-2">
                    <p class="text-center small pt-0">{{ legend }}</p>
                </b-col>
                <b-col md="3" class="my-1 text-right">
                    <b-dropdown
                        right
                        split
                        size="sm"
                        :text="perPage + ' éléments par page'"
                    >
                        <b-dropdown-item
                            v-for="pageOption in pageOptions"
                            :key="pageOption"
                            @click="setPerPage(pageOption)"
                        >
                            {{ pageOption }}
                        </b-dropdown-item>
                    </b-dropdown>
                </b-col>
            </b-row>
            <!-- /Pagination -->
        </div>
    </div>
</template>

<script>
import {mapState} from 'vuex'
import loading from '../../../common/components/loading.vue'
import dropMenuAction from '../components/dropMenuAction.vue'
import TableHandler from '../../../common/mixins/TableHandler.vue'
import DurationForm from '../components/actions/DurationForm.vue'
import ChargeTemplateHandler from '../components/actions/ChargeTemplateHandler.vue'

import ActionDateForm from '../components/actions/ActionDateForm.vue'

export default {
    name: 'TableActions',

    components: {
        ActionDateForm,
        loading,
        dropMenuAction,
        DurationForm,
        ChargeTemplateHandler,
    },

    mixins: [TableHandler],

    props: {
        client: {
            required: true,
            type: Number,
        },
    },

    data() {
        return {
            chargeTemplateId: null,
            fields: [
                {
                    key: 'menu',
                    label: '',
                },
                {
                    key: 'title',
                    label: 'Action',
                    class: 'td-sm',
                    sortable: true,
                },
                {
                    key: 'assigned_to',
                    label: 'A faire par',
                    class: 'td-sm',
                    sortable: true,
                },
                {
                    key: 'deadline_month_y0',
                    label: 'Année N',
                    class: 'td-sm text-left',
                    sortable: false,
                },
                {
                    key: 'deadline_month_y1',
                    label: 'Année N+1',
                    class: 'td-sm text-left',
                    sortable: false,
                },
                {
                    key: 'notification',
                    label: 'Notification',
                    class: 'td-sm text-right',
                    sortable: true,
                },
                {
                    key: 'duration',
                    label: 'Durée',
                    class: 'td-sm td-duration',
                    sortable: true,
                },
            ],
        }
    },

    computed: {
        ...mapState({
            year: (state) => state.browsing.year,
        }),
    },

    async created() {
        await this.checkUpdates()
        await this.getItems()
    },

    methods: {
        async getItems() {
            try {
                this.loading = true
                const response = await this.$axios.get(
                    `/clients/${this.client}/clientActions`
                )
                this.chargeTemplateId = response.data.meta.charge_template_id
                this.items = response.data.data.data
                this.items.sort((a, b) => a.title.localeCompare(b.title))
                this.filteredItems = this.items
                this.updateTotalRows()
                this.loading = false
            } catch (error) {
                // handle error
                console.log(error)
                this.handleErrors(error.response)
            }
        },

        async checkUpdates() {
            try {
                const response = await this.$axios.get(
                    `/clients/${this.client}/clientDeadlines/${this.year}/check-for-update`
                )
                this.$store.commit(
                    'browsing/SET_NEEDS_UPDATE',
                    response.data.needsUpdate
                )
                this.$store.commit(
                    'browsing/SET_ALL_ROLES_ASSIGNED',
                    response.data.allRolesAssigned
                )
            } catch (error) {
                // handle error
                console.log(error)
                this.handleErrors(error.response)
            }
        },
    },
}
</script>

<style>
.modal-backdrop {
    opacity: 0.5 !important;
}
</style>
